import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { modeLabel } from "./tierModeLabels";

type TierItem = {
  slug: string;
  label: string;
  monthly_limit: number | null;
  price_eur: number;
  allowed_modes: string[];
  updated_at: string | null;
  updated_by_email: string | null;
};

type TierListResp = {
  items: TierItem[];
  known_modes: string[];
  stripe_price_id_set: Record<string, boolean>;
};

type AuditEntry = {
  id: number;
  slug: string;
  old_label: string | null;
  new_label: string;
  old_limit: number | null;
  new_limit: number | null;
  old_price: number | null;
  new_price: number;
  old_modes: string[];
  new_modes: string[];
  changed_at: string;
  changed_by_email: string | null;
};

const TIER_ORDER = ["light", "basic", "full", "trial"] as const;

const ACCENT_BG: Record<string, string> = {
  light: "bg-[var(--cs-ivory)] text-[var(--cs-navy)]",
  basic: "bg-blue-100 text-blue-800",
  full: "bg-[var(--cs-rose)]/15 text-[var(--cs-rose-dark)]",
  trial: "bg-amber-100 text-amber-800",
};

function isUnusualLimit(n: number | null): boolean {
  if (n === null) return false;
  return n < 50 || n > 10000;
}

function isUnusualPrice(n: number): boolean {
  return n < 10 || n > 500;
}

export function AdminTierTab() {
  const [items, setItems] = useState<TierItem[]>([]);
  const [knownModes, setKnownModes] = useState<string[]>([]);
  const [stripeSet, setStripeSet] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await authFetchJson<{ items: AuditEntry[] }>("/api/admin/plan-tiers/audit?limit=50");
      setHistory(res.items ?? []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await authFetchJson<TierListResp>("/api/admin/plan-tiers");
      setItems(res.items ?? []);
      setKnownModes(res.known_modes ?? []);
      setStripeSet(res.stripe_price_id_set ?? {});
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onSaved = useCallback((updated: TierItem) => {
    setItems((prev) => prev.map((it) => (it.slug === updated.slug ? updated : it)));
    if (historyOpen) void loadHistory();
  }, [historyOpen, loadHistory]);

  const ordered = useMemo(() => {
    const bySlug = new Map(items.map((it) => [it.slug, it]));
    return TIER_ORDER.map((s) => bySlug.get(s)).filter(Boolean) as TierItem[];
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--cs-ink-muted)]">
          Wijzigingen gelden direct voor deze pod en binnen 30 seconden voor alle andere pods.
        </p>
        <button
          type="button"
          onClick={() => {
            const next = !historyOpen;
            setHistoryOpen(next);
            if (next) void loadHistory();
          }}
          className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
        >
          {historyOpen ? "Geschiedenis verbergen" : "Geschiedenis ▾"}
        </button>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {loadError}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {ordered.map((item) => (
            <TierCard
              key={item.slug}
              item={item}
              knownModes={knownModes}
              stripeSet={!!stripeSet[item.slug]}
              onSaved={onSaved}
            />
          ))}
        </div>
      )}

      {historyOpen ? (
        <HistoryDrawer entries={history} loading={historyLoading} />
      ) : null}
    </div>
  );
}

function TierCard({
  item, knownModes, stripeSet, onSaved,
}: {
  item: TierItem;
  knownModes: string[];
  stripeSet: boolean;
  onSaved: (it: TierItem) => void;
}) {
  const [label, setLabel] = useState(item.label);
  const [limit, setLimit] = useState<number>(item.monthly_limit ?? 0);
  const [unlimited, setUnlimited] = useState<boolean>(item.monthly_limit === null);
  const [price, setPrice] = useState<number>(item.price_eur);
  const [modes, setModes] = useState<Set<string>>(new Set(item.allowed_modes));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setLabel(item.label);
    setLimit(item.monthly_limit ?? 0);
    setUnlimited(item.monthly_limit === null);
    setPrice(item.price_eur);
    setModes(new Set(item.allowed_modes));
  }, [item.label, item.monthly_limit, item.price_eur, item.allowed_modes]);

  const dirty = (
    label !== item.label
    || (unlimited ? item.monthly_limit !== null : limit !== item.monthly_limit)
    || price !== item.price_eur
    || [...modes].sort().join("|") !== [...item.allowed_modes].sort().join("|")
  );

  const reset = () => {
    setLabel(item.label);
    setLimit(item.monthly_limit ?? 0);
    setUnlimited(item.monthly_limit === null);
    setPrice(item.price_eur);
    setModes(new Set(item.allowed_modes));
    setError("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {
        label,
        monthly_limit: unlimited ? null : limit,
        price_eur: price,
        allowed_modes: [...modes],
      };
      const updated = await authFetchJson<TierItem>(`/api/admin/plan-tiers/${item.slug}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      onSaved(updated);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const toggleMode = (mode: string) => {
    setModes((prev) => {
      const next = new Set(prev);
      if (next.has(mode)) next.delete(mode);
      else next.add(mode);
      return next;
    });
  };

  const emptyModesWarning = modes.size === 0;
  const limitWarning = !unlimited && isUnusualLimit(limit);
  const priceWarning = isUnusualPrice(price);

  return (
    <section className="glass-card overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] p-5">
      <header className="mb-4 flex items-center justify-between">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${ACCENT_BG[item.slug] ?? "bg-gray-100 text-gray-700"}`}>
          {item.slug}
        </span>
        {!stripeSet && item.slug !== "trial" ? (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
            ⚠ geen Stripe price_id gekoppeld
          </span>
        ) : null}
      </header>

      <div className="space-y-3 text-sm">
        <Field label="Label">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={80}
            className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5"
          />
        </Field>

        <Field label="Maandelijks limiet">
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              disabled={unlimited}
              value={unlimited ? "" : limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-28 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 tabular-nums disabled:opacity-40"
            />
            <label className="flex items-center gap-1.5 text-xs text-[var(--cs-ink-muted)]">
              <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
              Onbeperkt
            </label>
          </div>
        </Field>

        <Field label="Prijs (€/mnd)" hint="Alleen ter informatie — er is geen prijspagina; de afrekening loopt via Stripe (STRIPE_PRICE_ID_* env).">
          <input
            type="number"
            min={0}
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            className="w-28 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 tabular-nums"
          />
        </Field>

        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">Beschikbare modes</p>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            {knownModes.map((m) => (
              <label key={m} className="flex items-center gap-1.5">
                <input type="checkbox" checked={modes.has(m)} onChange={() => toggleMode(m)} />
                <span>{modeLabel(m)} <span className="text-[var(--cs-ink-muted)]">({m})</span></span>
              </label>
            ))}
          </div>
        </div>

        {(emptyModesWarning || limitWarning || priceWarning) ? (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
            {emptyModesWarning ? "Geen modes geselecteerd — gebruikers van deze tier krijgen niets. " : ""}
            {limitWarning ? "Ongebruikelijk limiet. " : ""}
            {priceWarning ? "Ongebruikelijke prijs. " : ""}
            Weet u zeker dat dit klopt?
          </p>
        ) : null}

        <p className="text-[11px] text-[var(--cs-ink-muted)]">
          Gewijzigd {formatDateTime(item.updated_at)}
          {item.updated_by_email ? ` · ${item.updated_by_email}` : ""}
        </p>

        {error ? (
          <p className="rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">{error}</p>
        ) : null}
      </div>

      <footer className="mt-4 flex items-center justify-end gap-2">
        {savedFlash ? <span className="mr-2 text-xs text-green-700">✓ opgeslagen</span> : null}
        <button
          type="button"
          onClick={reset}
          disabled={!dirty || saving}
          className="rounded-full border border-[rgba(10,52,96,0.18)] bg-white px-4 py-1.5 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-40"
        >
          Herstellen
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="rounded-full bg-[var(--cs-rose)] px-4 py-1.5 text-xs font-medium text-white shadow-[0_8px_20px_rgba(225,29,72,0.20)] hover:bg-[var(--cs-rose-dark)] disabled:opacity-40"
        >
          {saving ? "Opslaan…" : "Opslaan"}
        </button>
      </footer>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">{label}</span>
        {hint ? <span title={hint} className="cursor-help text-[var(--cs-ink-muted)]">ⓘ</span> : null}
      </div>
      {children}
    </div>
  );
}

function HistoryDrawer({ entries, loading }: { entries: AuditEntry[]; loading: boolean }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">Geschiedenis</h3>
      <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
        <table className="w-full text-xs">
          <thead className="bg-[var(--cs-ivory)] text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
            <tr>
              <th className="px-4 py-2 text-left">Wanneer</th>
              <th className="px-4 py-2 text-left">Wie</th>
              <th className="px-4 py-2 text-left">Tier</th>
              <th className="px-4 py-2 text-left">Veld</th>
              <th className="px-4 py-2 text-left">Oud → Nieuw</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-3 text-[var(--cs-ink-muted)]">Laden…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-3 text-[var(--cs-ink-muted)]">Geen wijzigingen.</td></tr>
            ) : entries.flatMap((e) => entryRows(e))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function fmtMaybeNumber(n: number | null): string {
  return n === null ? "onbeperkt" : String(n);
}

function entryRows(e: AuditEntry): ReactNode[] {
  const rows: ReactNode[] = [];
  const base = (
    <>
      <td className="px-4 py-2">{formatDateTime(e.changed_at)}</td>
      <td className="px-4 py-2">{e.changed_by_email ?? "—"}</td>
      <td className="px-4 py-2">{e.slug}</td>
    </>
  );

  if (e.old_label !== e.new_label) {
    rows.push(
      <tr key={`${e.id}-label`}>{base}
        <td className="px-4 py-2">label</td>
        <td className="px-4 py-2">{e.old_label ?? "—"} → {e.new_label}</td>
      </tr>
    );
  }
  if (e.old_limit !== e.new_limit) {
    rows.push(
      <tr key={`${e.id}-limit`}>{base}
        <td className="px-4 py-2">limiet</td>
        <td className="px-4 py-2 tabular-nums">{fmtMaybeNumber(e.old_limit)} → {fmtMaybeNumber(e.new_limit)}</td>
      </tr>
    );
  }
  if (e.old_price !== e.new_price) {
    rows.push(
      <tr key={`${e.id}-price`}>{base}
        <td className="px-4 py-2">prijs</td>
        <td className="px-4 py-2 tabular-nums">€{e.old_price ?? "—"} → €{e.new_price}</td>
      </tr>
    );
  }
  const oldSet = new Set(e.old_modes ?? []);
  const newSet = new Set(e.new_modes ?? []);
  const sameModes = oldSet.size === newSet.size && [...oldSet].every((m) => newSet.has(m));
  if (!sameModes) {
    rows.push(
      <tr key={`${e.id}-modes`}>{base}
        <td className="px-4 py-2">modes</td>
        <td className="px-4 py-2">{(e.old_modes ?? []).join(", ") || "—"} → {(e.new_modes ?? []).join(", ") || "—"}</td>
      </tr>
    );
  }
  if (rows.length === 0) {
    rows.push(
      <tr key={`${e.id}-noop`}>{base}
        <td className="px-4 py-2">—</td>
        <td className="px-4 py-2 text-[var(--cs-ink-muted)]">(geen wijziging)</td>
      </tr>
    );
  }
  return rows;
}
